import { type ConfirmOptions, type ConfirmState } from './confirm-store.types';

const CLOSED: ConfirmState = {
  open: false,
  options: {},
  isPending: false,
  error: null,
  typed: '',
};

/** Whether the typed phrase, if one is demanded, matches. */
function isUnlocked(state: ConfirmState): boolean {
  const { confirmPhrase } = state.options;
  return confirmPhrase === undefined || state.typed.trim() === confirmPhrase.trim();
}

let state: ConfirmState = CLOSED;
let resolveAnswer: ((confirmed: boolean) => void) | null = null;

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function setState(next: ConfirmState): void {
  state = next;
  emit();
}

/** Settles the outstanding question exactly once. */
function settle(confirmed: boolean): void {
  const resolve = resolveAnswer;
  resolveAnswer = null;
  resolve?.(confirmed);
}

function messageOf(reason: unknown): string {
  return reason instanceof Error && reason.message !== '' ? reason.message : '';
}

/**
 * The promise-API confirmation dialog (W9).
 *
 * An external store rather than a redux slice for a decisive reason: it holds a
 * PROMISE RESOLVER and an async `onConfirm` callback, neither of which is
 * serializable and neither of which belongs in a reducer. `ConfirmationRoot`
 * reads it through `useSyncExternalStore`, so a confirmation re-renders one
 * component instead of every connected surface in the shell.
 *
 * A single question at a time: a second `confirm()` supersedes the first, which
 * resolves `false`. Leaving it pending would strand its `await` forever.
 */
export const confirmStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot(): ConfirmState {
    return state;
  },

  confirm(options: ConfirmOptions): Promise<boolean> {
    // Supersede whatever was asked before, so no caller waits on a dialog the
    // operator can no longer see.
    settle(false);

    return new Promise<boolean>((resolve) => {
      resolveAnswer = resolve;
      setState({ open: true, options, isPending: false, error: null, typed: '' });
    });
  },

  /**
   * Accepts the question.
   *
   * With an `onConfirm`, the dialog stays open and pending until it settles —
   * and stays open on failure, holding the message. Closing on failure looks
   * exactly like closing on success, which is how a delete that did not happen
   * gets reported as done.
   */
  async accept(): Promise<void> {
    if (!state.open || state.isPending) return;

    // Guarded in the store, not only by a disabled button: the button is the
    // affordance, this is the rule. A keyboard submit or a second subscriber
    // would otherwise walk straight past it.
    if (!isUnlocked(state)) return;

    const { onConfirm } = state.options;

    if (onConfirm === undefined) {
      setState(CLOSED);
      settle(true);
      return;
    }

    setState({ ...state, isPending: true, error: null });

    try {
      await onConfirm();
      setState(CLOSED);
      settle(true);
    } catch (reason) {
      setState({ ...state, isPending: false, error: messageOf(reason) });
    }
  },

  /** Records what the operator has typed against `confirmPhrase`. */
  type(value: string): void {
    if (!state.open || state.isPending) return;

    setState({ ...state, typed: value });
  },

  /** Whether the confirm button should be enabled. */
  isUnlocked(): boolean {
    return isUnlocked(state);
  },

  /** Dismisses, unless a mutation is already in flight. */
  cancel(): void {
    if (!state.open || state.isPending) return;

    setState(CLOSED);
    settle(false);
  },

  /** Test seam, and the sign-out path. */
  reset(): void {
    resolveAnswer = null;
    setState(CLOSED);
  },
};
